import React, { useEffect, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, Minus, RefreshCw, AlertCircle } from "lucide-react";
import { adminApi } from "../../../lib/api";
import NotConnected from "./NotConnected";
import Ga4KeyUpload from "./Ga4KeyUpload";
import MeasurementProtocolCard from "./MeasurementProtocolCard";

const PERIODS = [7, 28, 90, 365];

const LABELS = {
  totalUsers: "Users",
  sessions: "Sessions",
  screenPageViews: "Page views",
  engagedSessions: "Engaged sessions",
  engagementRate: "Engagement rate",
  averageSessionDuration: "Average session",
  keyEvents: "Key events",
};

const fmt = (key, v) => {
  if (v === null || v === undefined) return "—";
  if (key === "engagementRate") return `${(v * 100).toFixed(1)}%`;
  if (key === "averageSessionDuration") return `${Math.floor(v / 60)}m ${Math.round(v % 60)}s`;
  return Math.round(v).toLocaleString();
};

const Trend = ({ m }) => {
  if (!m || m.pct === null || m.pct === undefined) return <span className="text-[11px] text-slate-400">no comparison</span>;
  const Icon = m.direction === "up" ? ArrowUpRight : m.direction === "down" ? ArrowDownRight : Minus;
  const tone = m.direction === "up" ? "text-emerald-600" : m.direction === "down" ? "text-red-500" : "text-slate-400";
  return <span className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${tone}`}><Icon size={12} /> {Math.abs(m.pct)}%</span>;
};

const Table = ({ title, rows, columns, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid={testid}>
    <p className="text-xs font-bold text-navy px-4 pt-4">{title}</p>
    <table className="w-full text-sm mt-2 min-w-[420px]">
      <thead>
        <tr className="text-left text-slate-400 border-b border-slate-100">
          <th className="py-2 px-4 font-semibold">{columns[0]}</th>
          {columns.slice(1).map((c) => <th key={c} className="py-2 px-4 font-semibold text-right">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {(rows || []).slice(0, 10).map((r) => (
          <tr key={r.key} className="border-b border-slate-50">
            <td className="py-2 px-4 text-navy [overflow-wrap:anywhere]">{r.key || "(not set)"}</td>
            {Object.keys(r).filter((k) => k !== "key").map((k) => (
              <td key={k} className="py-2 px-4 text-right text-slate-500 tabular-nums">{fmt(k, r[k])}</td>
            ))}
          </tr>
        ))}
        {(rows || []).length === 0 && (
          <tr><td className="py-3 px-4 text-xs text-slate-400" colSpan={columns.length}>Nothing in this period.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default function WebsiteAnalyticsTab() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d = days) => {
    setLoading(true);
    adminApi.ga4.status().then(setStatus).catch(() => setStatus(null));
    adminApi.reporting
      .ga4(d)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading && !data) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!data) return <p className="text-sm text-red-500">GA4 reporting could not be loaded.</p>;

  if (!data.connected) {
    return (
      <div className="space-y-4">
        <NotConnected
          title="Google Analytics 4 is not connected for reading"
          reason="Measurement is live on the site through Tag Manager, so visits are being recorded in GA4. Reading those figures back into this dashboard needs the Analytics Data API, which is a separate connection."
          needs={data.needs || []}
          links={[
            { href: "https://analytics.google.com/analytics/web/#/admin", label: "Open GA4 admin" },
            { href: "https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com", label: "Enable the Data API" },
            { href: "https://console.cloud.google.com/iam-admin/serviceaccounts", label: "Service accounts" },
          ]}
          where={data.property_id ? `Property ${data.property_id} is configured; the service account key is what's outstanding.` : undefined}
          testid="ga4-tab-not-connected"
        />
        <Ga4KeyUpload status={status} onChange={() => load(days)} />
        <MeasurementProtocolCard status={status} onChange={() => load(days)} />
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="space-y-6" data-testid="ga4-report">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`ga4-period-${d}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold duration-200 [transition-property:background-color,color] ${
              days === d ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {d === 365 ? "12 months" : `${d} days`}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(days)}
          data-testid="ga4-refresh"
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy duration-200 [transition-property:color]"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {data.error ? (
        <div className="bg-white rounded-xl border border-red-200 p-5 flex items-start gap-3" data-testid="ga4-error">
          <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-navy text-sm">Google returned an error</p>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{data.error}</p>
            <p className="text-[11px] text-slate-400 mt-2">Property {data.property_id}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.keys(LABELS).map((k) => (
              <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ga4-metric-${k}`}>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{LABELS[k]}</p>
                <p className="text-2xl font-extrabold text-navy mt-1 tabular-nums">{fmt(k, s[k]?.value)}</p>
                <Trend m={s[k]} />
              </div>
            ))}
          </div>

          {(data.series || []).length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 h-64" data-testid="ga4-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="ga4users" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Area type="monotone" dataKey="totalUsers" stroke="#f97316" strokeWidth={2} fill="url(#ga4users)" name="Users" />
                  <Area type="monotone" dataKey="sessions" stroke="#94a3b8" strokeWidth={1} fill="transparent" name="Sessions" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <Table title="Traffic channels" rows={data.channels} columns={["Channel", "Sessions", "Users", "Key events"]} testid="ga4-channels" />
            <Table title="New vs returning" rows={data.new_vs_returning} columns={["Visitor", "Users", "Sessions"]} testid="ga4-new-returning" />
            <Table title="Source / medium" rows={data.sources} columns={["Source", "Sessions", "Key events"]} testid="ga4-sources" />
            <Table title="Landing pages" rows={data.landing_pages} columns={["Page", "Sessions", "Engagement", "Key events"]} testid="ga4-landing" />
            <Table title="Devices" rows={data.devices} columns={["Device", "Sessions", "Users"]} testid="ga4-devices" />
            <Table title="Countries" rows={data.countries} columns={["Country", "Sessions", "Users"]} testid="ga4-countries" />
            <div className="lg:col-span-2">
              <Table title="Events" rows={data.events} columns={["Event", "Count"]} testid="ga4-events" />
            </div>
          </div>
        </>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Property {data.property_id} · {data.period?.start} to {data.period?.end} · last read{" "}
        {data.fetched_at ? new Date(data.fetched_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
        {data.from_cache ? " (cached)" : ""}. {data.note}
      </p>

      <MeasurementProtocolCard status={status} onChange={() => load(days)} />

      {status?.credentials_source === "uploaded" && (
        <details className="group" data-testid="ga4-key-manage">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-400 hover:text-navy duration-200 [transition-property:color]">
            Service account key
          </summary>
          <div className="mt-3">
            <Ga4KeyUpload status={status} onChange={() => load(days)} />
          </div>
        </details>
      )}
    </div>
  );
}
